import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import Footer from "@/components/Footer";
import { useCookieConsent } from "@/contexts/CookieConsentContext";
import { generatePageTitle, generateCanonicalUrl } from "@/lib/seoConfig";

const DoNotSell = () => {
  const { consent, rejectAll } = useCookieConsent();
  const [confirmed, setConfirmed] = useState(false);

  const isOptedOut =
    consent !== null && consent.analytics === false && consent.marketing === false;

  const handleOptOut = () => {
    rejectAll();
    setConfirmed(true);
  };

  return (
    <>
      <Helmet>
        <title>{generatePageTitle("Do Not Sell or Share My Personal Information")}</title>
        <meta name="description" content="California residents may opt out of the sale or sharing of their personal information by iQ Texting." />
        <link rel="canonical" href={generateCanonicalUrl("/do-not-sell")} />
        <meta property="og:title" content={generatePageTitle("Do Not Sell or Share My Personal Information")} />
        <meta property="og:url" content={generateCanonicalUrl("/do-not-sell")} />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
        <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="container mx-auto px-4 py-6">
            <Button variant="ghost" asChild className="mb-4">
              <Link to="/">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
              </Link>
            </Button>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
              Do Not Sell or Share My Personal Information
            </h1>
            <p className="text-muted-foreground mt-2">Last updated: {new Date().toLocaleDateString()}</p>
          </div>
        </header>

        <main className="container mx-auto px-4 py-12 max-w-4xl">
          <div className="prose prose-slate dark:prose-invert max-w-none space-y-8">
            <p className="text-muted-foreground">
              This page is maintained by iQ Texting in accordance with the California Consumer Privacy
              Act (CCPA) and the California Privacy Rights Act (CPRA).
            </p>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Your right</h2>
              <p className="text-muted-foreground">
                If you are a California resident, you have the right to opt out of the "sale" or "sharing"
                of your personal information. Under the CPRA, "sharing" includes disclosing personal
                information to third parties for cross-context behavioral advertising, even when no money
                changes hands.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">What iQ Texting does</h2>
              <p className="text-muted-foreground">
                We do not sell your personal information for money. We may use analytics and marketing
                cookies that could qualify as "sharing" under the CPRA. You can turn these off at any time
                using the button below.
              </p>
            </section>

            <section className="rounded-lg border p-6 bg-card not-prose">
              <h2 className="text-2xl font-semibold mb-4">Opt out now</h2>
              <p className="text-foreground mb-4">
                Current status for this browser:{" "}
                <span className={isOptedOut ? "text-primary font-semibold" : "font-semibold"}>
                  {isOptedOut ? "Opted out" : "Not opted out"}
                </span>
              </p>
              <Button onClick={handleOptOut} size="lg">
                {isOptedOut ? "Reconfirm opt-out" : "Opt out of sale and sharing"}
              </Button>
              {confirmed && (
                <p className="text-sm text-primary mt-4" role="status">
                  Your preference has been saved on this browser. Analytics and marketing cookies are now off.
                </p>
              )}
              <p className="text-sm text-muted-foreground mt-4">
                Note: This setting is stored in your browser. If you clear cookies or use a different
                browser or device, you'll need to opt out again.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Other ways to opt out</h2>
              <p className="text-muted-foreground">
                To opt out across all channels — including any future email marketing — email{" "}
                <a className="underline text-primary" href="mailto:privacy@iqtexting.com">privacy@iqtexting.com</a>{" "}
                with the subject line "CCPA Opt-Out Request" and the email address associated with your account.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Authorized agents</h2>
              <p className="text-muted-foreground">
                You may designate an authorized agent to submit this request on your behalf. We may
                require written proof of the agent's authorization and verification of your identity
                before processing the request.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Contact</h2>
              <p className="text-muted-foreground">
                Questions about your privacy rights? Email{" "}
                <a className="underline text-primary" href="mailto:privacy@iqtexting.com">privacy@iqtexting.com</a>.
              </p>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default DoNotSell;
